/**
 * WebSocket Service for Real-time Ticket Updates
 *
 * This service manages WebSocket connections for real-time ticket notifications.
 * It handles connection lifecycle, reconnection logic, and message distribution.
 */

class TicketWebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000; // 3 seconds
    this.listeners = new Map();
    this.isConnecting = false;
    this.shouldReconnect = true;
  }

  /**
   * Get WebSocket URL based on current environment
   */
  getWebSocketUrl() {
    // Determine if we're using HTTPS or HTTP
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    // Check if running locally or in production
    const isDevelopment = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1';

    if (isDevelopment) {
      // Development - connect to Django dev server
      return `ws://127.0.0.1:8000/ws/tickets/`;
    } else {
      // Production - use current host
      return `${protocol}//${window.location.host}/ws/tickets/`;
    }
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('WebSocket connection in progress');
      return;
    }

    try {
      this.isConnecting = true;
      const token = localStorage.getItem('jwt_token');

      if (!token) {
        console.warn('No JWT token found, skipping WebSocket connection');
        this.isConnecting = false;
        return;
      }

      const wsUrl = this.getWebSocketUrl();
      console.log('Connecting to WebSocket:', wsUrl);

      // Create WebSocket connection with auth token
      this.ws = new WebSocket(`${wsUrl}?token=${token}`);

      this.ws.onopen = () => {
        console.log('WebSocket connected successfully');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.notifyListeners('connection', { status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          console.log('[WS] Raw message received');
          console.log('[WS] event.data type:', typeof event.data);
          console.log('[WS] event.data instanceof Blob:', event.data instanceof Blob);
          console.log('[WS] event.data instanceof ArrayBuffer:', event.data instanceof ArrayBuffer);

          if (event.data instanceof Blob) {
            console.warn('[WS] Received binary Blob data instead of string. Size:', event.data.size, 'bytes');
            event.data.arrayBuffer().then(buf => {
              const bytes = new Uint8Array(buf);
              console.warn('[WS] First 16 bytes (hex):', Array.from(bytes.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' '));
            });
            return;
          }

          if (event.data instanceof ArrayBuffer) {
            const bytes = new Uint8Array(event.data);
            console.warn('[WS] Received raw ArrayBuffer data. First 16 bytes (hex):', Array.from(bytes.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' '));
            return;
          }

          console.log('[WS] Raw string (first 200 chars):', String(event.data).substring(0, 200));
          const data = JSON.parse(event.data);
          console.log('[WS] Parsed message:', data);
          this.handleMessage(data);
        } catch (error) {
          console.error('[WS] Error parsing WebSocket message:', error);
          console.error('[WS] Offending data:', event.data);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.notifyListeners('error', { error });
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        this.notifyListeners('connection', { status: 'disconnected' });

        // Attempt to reconnect if not manually closed
        if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
          setTimeout(() => this.connect(), this.reconnectDelay);
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('Max reconnection attempts reached');
          this.notifyListeners('connection', { status: 'failed' });
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.isConnecting = false;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(data) {
    const { type, payload } = data;

    switch (type) {
      case 'ticket_created':
        this.notifyListeners('ticket_created', payload);
        break;

      case 'ticket_updated':
        this.notifyListeners('ticket_updated', payload);
        break;

      case 'ticket_deleted':
        this.notifyListeners('ticket_deleted', payload);
        break;

      case 'comment_added':
        this.notifyListeners('comment_added', payload);
        break;

      case 'comment_deleted':
        this.notifyListeners('comment_deleted', payload);
        break;

      case 'attachment_added':
        this.notifyListeners('attachment_added', payload);
        break;

      case 'attachment_deleted':
        this.notifyListeners('attachment_deleted', payload);
        break;

      default:
        console.warn('Unknown WebSocket message type:', type);
    }
  }

  /**
   * Subscribe to WebSocket events
   * @param {string} event - Event name (e.g., 'ticket_updated', 'comment_added')
   * @param {Function} callback - Callback function to execute
   * @returns {Function} Unsubscribe function
   */
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
      }
    };
  }

  /**
   * Notify all listeners for a specific event
   */
  notifyListeners(event, data) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket listener callback:', error);
        }
      });
    }
  }

  /**
   * Send a message through WebSocket
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket is not connected. Cannot send message:', data);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.shouldReconnect = false;

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.reconnectAttempts = 0;
    console.log('WebSocket disconnected');
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection status
   */
  getStatus() {
    if (!this.ws) return 'disconnected';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }
}

// Create singleton instance
const ticketWebSocketService = new TicketWebSocketService();

export default ticketWebSocketService;
